'use client';

import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { type AxiosError } from 'axios';
import { Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { triggerTapHaptic } from '@/core/lib/dnd-haptics';

interface RemoveMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  memberId: string;
  memberName: string;
  onSuccess: () => void;
  dict: {
    title: string;
    description: string;
    seatImpact: string;
    confirm: string;
    removing: string;
    cancel: string;
    success: string;
    error: string;
    cannotRemoveOwner: string;
    cannotRemoveSelf: string;
  };
  removeMember: (workspaceId: string, memberId: string) => Promise<void>;
}

export function RemoveMemberDialog({
  open,
  onOpenChange,
  workspaceId,
  memberId,
  memberName,
  onSuccess,
  dict,
  removeMember,
}: RemoveMemberDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await removeMember(workspaceId, memberId);
      toast.success(dict.success);
      onOpenChange(false);
      onSuccess();
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      console.error('Failed to remove member:', error);
      const errorMessage = axiosError?.message || dict.error;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[500px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {dict.description.replace('{name}', memberName)}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="py-4">
          <Alert>
            <Users className="h-4 w-4" />
            <AlertDescription>{dict.seatImpact}</AlertDescription>
          </Alert>
        </div>

        <ResponsiveDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {dict.cancel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? dict.removing : dict.confirm}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
